import { useCallback, useEffect } from 'react';
import {
  AutoSaveNoteDto,
  AutoSaveNoteQueryDto,
} from '@act/shared/data-transfer-objects';
import { autoSaveNotesRequests } from 'redux/requests/autoSave.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';

export interface UseNoteDraftOptions {
  /** Member the draft belongs to. */
  subjectId: string;
  /** Draft bucket, e.g. 'notes' | 'visits' | 'events'. */
  type: string;
  /** Distinguishes concurrent drafts of the same type for one member. */
  draftId?: string;
  /** Only load/persist a draft when true (typically create-mode only). */
  enabled?: boolean;
}

export interface UseNoteDraftResult {
  draft: AutoSaveNoteDto | null;
  isLoading: boolean;
  isSaving: boolean;
  saveError: unknown;
  /** Persist the draft (fire-and-forget). */
  saveDraft: (text: string) => void;
  /** Remove the draft (e.g. after the real note is created or discarded). */
  clearDraft: () => void;
}

/**
 * Manages the server-side autosave draft for create flows that have no parent
 * entity yet (e.g. a new member note). Loads any existing draft on mount so the
 * caller can offer recovery, and persists/clears it through `/notes/auto-save`.
 * Built on `useApiCall`.
 */
export function useNoteDraft({
  subjectId,
  type,
  draftId,
  enabled = true,
}: UseNoteDraftOptions): UseNoteDraftResult {
  const [draft, isLoading, getDraft] = useApiCall(
    autoSaveNotesRequests.getAutoSavedNote
  );
  const [, isPosting, postDraft, postError] = useApiCall(
    autoSaveNotesRequests.postAutoSavedNote
  );
  const [, , deleteDraft] = useApiCall(
    autoSaveNotesRequests.deleteAutoSavedNote
  );

  const buildParams = useCallback(
    (): AutoSaveNoteQueryDto => ({
      subjectId,
      type,
      ...(draftId ? { draftId } : {}),
    }),
    [subjectId, type, draftId]
  );

  useEffect(() => {
    if (!enabled || !subjectId) return;
    getDraft({ params: buildParams() });
    // `getDraft` is a fresh closure each render; depend only on the query.
  }, [enabled, subjectId, type, draftId]);

  const saveDraft = useCallback(
    (text: string) => {
      postDraft({ params: buildParams(), body: { text } });
    },
    [buildParams]
  );

  const clearDraft = useCallback(() => {
    deleteDraft({ params: buildParams() });
  }, [buildParams]);

  return {
    draft: draft ?? null,
    isLoading: !!isLoading,
    isSaving: !!isPosting,
    saveError: postError,
    saveDraft,
    clearDraft,
  };
}

export default useNoteDraft;
